import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ExperienciasService from '../../service/experiencias/ExperienciasService';

// =============================================================================
// COMPONENTE: SEÇÃO DE EXPERIÊNCIAS (DESIGNAÇÕES + EVENTOS)
// =============================================================================
// - Renderiza DUAS seções empilhadas:
//   1) Experiências vindas das designações (com filtros: data, ponto, nome)
//   2) Experiências vindas dos eventos      (com filtros: evento, nome)
// - Layout segue o padrão do projeto (styled-components, cores #008080)
// - Lista scrollável com altura fixa para comportar muitos relatos
// =============================================================================

// Limites de data para o filtro (YYYY-MM-DD)
const hojeISO = () => new Date().toISOString().slice(0, 10);
const diasAtrasISO = (d) => {
  const t = new Date();
  t.setDate(t.getDate() - d);
  return t.toISOString().slice(0, 10);
};

// Formata data ISO para DD/MM/AAAA (pt-BR)
function formatarDataBR(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return '';
  }
}

// Concatena horários em string amigável
function formatarHorarios(horarios) {
  if (!Array.isArray(horarios) || horarios.length === 0) return '';
  return horarios.join(', ');
}

// =============================================================================
// STYLED COMPONENTS (paleta do projeto: verde-azulado #008080)
// =============================================================================
const Secao = styled.section`
  margin-top: 32px;
  padding: 24px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const CabecalhoSecao = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: 600;
  color: #008080;

  &::before {
    content: '';
    width: 4px;
    height: 20px;
    background: #008080;
    border-radius: 4px;
  }
`;

const Filtros = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const FiltroItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 160px;
  flex: 1;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #555;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
  outline: none;
  transition: border-color 0.2s;
  &:focus { border-color: #008080; }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
  outline: none;
  &:focus { border-color: #008080; }
`;

const BotaoLimpar = styled.button`
  padding: 8px 16px;
  background: transparent;
  color: #008080;
  border: 1px solid #008080;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  height: 36px;
  transition: background-color 0.2s;
  &:hover { background: #e0f2f1; }
`;

const ListaScroll = styled.div`
  max-height: 480px;
  overflow-y: auto;
  padding-right: 4px;

  /* Scrollbar estilizada (webkit) */
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: #008080; border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: #005959; }
`;

const Card = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 16px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  margin-bottom: 12px;
  background: #fff;
  transition: box-shadow 0.2s, transform 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ResumoEsquerda = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-right: 16px;
  border-right: 1px solid #f0f0f0;

  @media (max-width: 768px) {
    border-right: none;
    padding-right: 0;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
  }
`;

const LinhaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #555;
`;

const NomePublicador = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #008080;
  margin-bottom: 2px;
`;

const ExperienciaTexto = styled.div`
  font-size: 15px;
  color: #333;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;

const EstadoVazio = styled.div`
  padding: 32px;
  text-align: center;
  color: #999;
  font-style: italic;
`;

const MensagemErro = styled.div`
  padding: 12px 16px;
  background: #fee;
  color: #c33;
  border-radius: 6px;
  margin-bottom: 12px;
`;

const Loading = styled.div`
  padding: 16px;
  text-align: center;
  color: #666;
  font-style: italic;
`;

const ContadorResultados = styled.div`
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
`;

// =============================================================================
// COMPONENTE: ExperienciasSection (exportação default)
// =============================================================================
export default function ExperienciasSection() {
  const [experienciasDesignacoes, setExperienciasDesignacoes] = useState([]);
  const [experienciasEventos, setExperienciasEventos] = useState([]);
  const [carregandoDesignacoes, setCarregandoDesignacoes] = useState(true);
  const [carregandoEventos, setCarregandoEventos] = useState(true);
  const [erroDesignacoes, setErroDesignacoes] = useState(null);
  const [erroEventos, setErroEventos] = useState(null);

  // Filtros da seção de designações
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [filtroPonto, setFiltroPonto] = useState('todos');
  const [buscaNome, setBuscaNome] = useState('');

  // Filtros da seção de eventos
  const [filtroEvento, setFiltroEvento] = useState('todos');
  const [buscaNomeEvento, setBuscaNomeEvento] = useState('');

  // Carrega experiencias ao montar
  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      try {
        const designacoes = await ExperienciasService.listarExperienciasDesignacoes();
        if (!cancelado) {
          setExperienciasDesignacoes(designacoes);
          setErroDesignacoes(null);
        }
      } catch (e) {
        if (!cancelado) setErroDesignacoes('Não foi possível carregar experiências das designações.');
      } finally {
        if (!cancelado) setCarregandoDesignacoes(false);
      }

      try {
        const eventos = await ExperienciasService.listarExperienciasEventos();
        if (!cancelado) {
          setExperienciasEventos(eventos);
          setErroEventos(null);
        }
      } catch (e) {
        if (!cancelado) setErroEventos('Não foi possível carregar experiências dos eventos.');
      } finally {
        if (!cancelado) setCarregandoEventos(false);
      }
    };
    carregar();
    return () => { cancelado = true; };
  }, []);

  // Lista de pontos únicos para o filtro
  const pontosUnicos = useMemo(() => {
    const set = new Set();
    experienciasDesignacoes.forEach((e) => {
      if (e.ponto) set.add(e.ponto);
    });
    return Array.from(set).sort();
  }, [experienciasDesignacoes]);

  // Lista de eventos únicos para o filtro
  const eventosUnicos = useMemo(() => {
    const set = new Set();
    experienciasEventos.forEach((e) => {
      if (e.eventoNome) set.add(e.eventoNome);
    });
    return Array.from(set).sort();
  }, [experienciasEventos]);

  // Filtro aplicado para designações
  const experienciasDesignacoesFiltradas = useMemo(() => {
    return experienciasDesignacoes.filter((e) => {
      // Filtro por data (início)
      if (filtroDataInicio) {
        const dataItem = (e.data || '').slice(0, 10);
        if (dataItem < filtroDataInicio) return false;
      }
      // Filtro por data (fim)
      if (filtroDataFim) {
        const dataItem = (e.data || '').slice(0, 10);
        if (dataItem > filtroDataFim) return false;
      }
      // Filtro por ponto
      if (filtroPonto !== 'todos' && e.ponto !== filtroPonto) return false;
      // Filtro por nome
      if (buscaNome) {
        const n = (e.nome || '').toLowerCase();
        if (!n.includes(buscaNome.toLowerCase())) return false;
      }
      return true;
    });
  }, [experienciasDesignacoes, filtroDataInicio, filtroDataFim, filtroPonto, buscaNome]);

  // Filtro aplicado para eventos
  const experienciasEventosFiltradas = useMemo(() => {
    return experienciasEventos.filter((e) => {
      if (filtroEvento !== 'todos' && e.eventoNome !== filtroEvento) return false;
      if (buscaNomeEvento) {
        const n = (e.nome || '').toLowerCase();
        if (!n.includes(buscaNomeEvento.toLowerCase())) return false;
      }
      return true;
    });
  }, [experienciasEventos, filtroEvento, buscaNomeEvento]);

  // Helpers de limpeza de filtros
  const limparFiltrosDesignacao = () => {
    setFiltroDataInicio('');
    setFiltroDataFim('');
    setFiltroPonto('todos');
    setBuscaNome('');
  };
  const limparFiltrosEvento = () => {
    setFiltroEvento('todos');
    setBuscaNomeEvento('');
  };

  return (
    <>
      {/* ========================================================== */}
      {/* SEÇÃO 1: EXPERIÊNCIAS DAS DESIGNAÇÕES                       */}
      {/* ========================================================== */}
      <Secao>
        <CabecalhoSecao>Experiências das Designações</CabecalhoSecao>

        <Filtros>
          <FiltroItem>
            <Label>Data inicial</Label>
            <Input
              type="date"
              value={filtroDataInicio}
              onChange={(e) => setFiltroDataInicio(e.target.value)}
            />
          </FiltroItem>
          <FiltroItem>
            <Label>Data final</Label>
            <Input
              type="date"
              value={filtroDataFim}
              onChange={(e) => setFiltroDataFim(e.target.value)}
            />
          </FiltroItem>
          <FiltroItem>
            <Label>Ponto</Label>
            <Select value={filtroPonto} onChange={(e) => setFiltroPonto(e.target.value)}>
              <option value="todos">Todos os pontos</option>
              {pontosUnicos.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </Select>
          </FiltroItem>
          <FiltroItem>
            <Label>Buscar por nome</Label>
            <Input
              type="text"
              placeholder="Ex: João Silva"
              value={buscaNome}
              onChange={(e) => setBuscaNome(e.target.value)}
            />
          </FiltroItem>
          <BotaoLimpar onClick={limparFiltrosDesignacao} title="Limpar filtros">
            Limpar
          </BotaoLimpar>
        </Filtros>

        {erroDesignacoes && <MensagemErro>{erroDesignacoes}</MensagemErro>}

        {carregandoDesignacoes ? (
          <Loading>Carregando experiências das designações...</Loading>
        ) : experienciasDesignacoesFiltradas.length === 0 ? (
          <EstadoVazio>
            Nenhuma experiência de designação encontrada com os filtros atuais.
          </EstadoVazio>
        ) : (
          <>
            <ContadorResultados>
              Mostrando {experienciasDesignacoesFiltradas.length} de {experienciasDesignacoes.length} relato{experienciasDesignacoesFiltradas.length !== 1 ? 's' : ''}
            </ContadorResultados>
            <ListaScroll>
              {experienciasDesignacoesFiltradas.map((exp, idx) => (
                <Card key={`${exp.designacaoId}-${idx}`}>
                  <ResumoEsquerda>
                    <LinhaInfo>
                      <span>📅</span>
                      <strong>{formatarDataBR(exp.data)}</strong>
                    </LinhaInfo>
                    <LinhaInfo>
                      <span>📍</span>
                      <span>{exp.ponto || '—'}</span>
                    </LinhaInfo>
                    {exp.horario && exp.horario.length > 0 && (
                      <LinhaInfo>
                        <span>🕐</span>
                        <span>{formatarHorarios(exp.horario)}</span>
                      </LinhaInfo>
                    )}
                    <NomePublicador>{exp.nome || 'Anônimo'}</NomePublicador>
                    {exp.congregacao && (
                      <LinhaInfo>
                        <span>⛪</span>
                        <span>{exp.congregacao}</span>
                      </LinhaInfo>
                    )}
                  </ResumoEsquerda>
                  <ExperienciaTexto>
                    {exp.texto || 'Sem relato informado.'}
                  </ExperienciaTexto>
                </Card>
              ))}
            </ListaScroll>
          </>
        )}
      </Secao>

      {/* ========================================================== */}
      {/* SEÇÃO 2: EXPERIÊNCIAS DOS EVENTOS                          */}
      {/* ========================================================== */}
      <Secao>
        <CabecalhoSecao>Experiências dos Eventos</CabecalhoSecao>

        <Filtros>
          <FiltroItem>
            <Label>Evento</Label>
            <Select value={filtroEvento} onChange={(e) => setFiltroEvento(e.target.value)}>
              <option value="todos">Todos os eventos</option>
              {eventosUnicos.map((ev) => (
                <option key={ev} value={ev}>{ev}</option>
              ))}
            </Select>
          </FiltroItem>
          <FiltroItem>
            <Label>Buscar por nome</Label>
            <Input
              type="text"
              placeholder="Ex: Maria Souza"
              value={buscaNomeEvento}
              onChange={(e) => setBuscaNomeEvento(e.target.value)}
            />
          </FiltroItem>
          <BotaoLimpar onClick={limparFiltrosEvento} title="Limpar filtros">
            Limpar
          </BotaoLimpar>
        </Filtros>

        {erroEventos && <MensagemErro>{erroEventos}</MensagemErro>}

        {carregandoEventos ? (
          <Loading>Carregando experiências dos eventos...</Loading>
        ) : experienciasEventosFiltradas.length === 0 ? (
          <EstadoVazio>
            Nenhuma experiência de evento encontrada com os filtros atuais.
          </EstadoVazio>
        ) : (
          <>
            <ContadorResultados>
              Mostrando {experienciasEventosFiltradas.length} de {experienciasEventos.length} relato{experienciasEventosFiltradas.length !== 1 ? 's' : ''}
            </ContadorResultados>
            <ListaScroll>
              {experienciasEventosFiltradas.map((exp, idx) => (
                <Card key={`${exp.eventoId}-${idx}`}>
                  <ResumoEsquerda>
                    <LinhaInfo>
                      <strong>{exp.eventoNome || 'Evento'}</strong>
                    </LinhaInfo>
                    <LinhaInfo>
                      <span>📅</span>
                      <span>{formatarDataBR(exp.data)}</span>
                    </LinhaInfo>
                    <NomePublicador>{exp.nome || 'Anônimo'}</NomePublicador>
                    {exp.congregacao && (
                      <LinhaInfo>
                    <span>{exp.congregacao}</span>
                      </LinhaInfo>
                    )}
                  </ResumoEsquerda>
                  <ExperienciaTexto>
                    {exp.texto || 'Sem relato informado.'}
                  </ExperienciaTexto>
                </Card>
              ))}
            </ListaScroll>
          </>
        )}
      </Secao>
    </>
  );
}
